import React, { useEffect, useState } from 'react';
import Folder from './Folder';
import { PlusFilledIcon } from '@/components/Icons';
import useFolderStore from '@/state/folder-store';
import { AddNewFolder, DeleteFolder, DownloadFolder, MoveAudioToFolder, RenameFolder, SetFolderSort } from '@/service/folder';
import { AudioDto } from '@/service/type';
import { useRefreshData } from '@/hooks/useRefreshData';
import { Dialog } from '@/components/Dialog';
import { Button } from '../SongDetail';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FolderInput } from './FolderInput';
import { useAddNewFolderShow } from '@/state/show-store';
import { downloadAndZipFiles } from '@/utils/download';

const Folders: React.FC = () => {
  const { folderState } = useFolderStore();
  const { folderId } = useParams<{ folderId: string }>();
  const { refreshFolders, refreshAudioInList, refreshSongDetail } = useRefreshData();

  useEffect(() => {
    refreshFolders();
  }, []);

  const onCreateFolder = async (folderName: string, parentId?: number) => {
    try {
      await AddNewFolder({ name: folderName, parentFolderId: parentId });
      await refreshFolders();
    } catch (error: any) {
      toast.error(error?.message || 'An error occurred when downloading track.');
    }
  };
  const navigate = useNavigate();

  const onDeleteFolder = async (id: number) => {
    Dialog.info({
      closeClassName: 'hidden',
      dialogContentClassName: 'w-[330px] outline-none',
      title: 'Are you sure you want to delete this folder?',
      contentJSX: ({ onClose }) => {
        return (
          <div className=" p-jen-spacing-16 text-jen-content-secondary">
            <div className=" text-jen-size-heading-05">
              Deleting this folder will move all tracks with the folder to your archived tracks.
            </div>
            <Button
              onClick={async () => {
                try {
                  await DeleteFolder({ folderId: id });
                  await refreshFolders();
                  toast('delete folder success!');
                  onClose();
                  if (Number(folderId) === id) {
                    navigate(`/`);
                  }
                } catch (error) {
                  toast.error('delete error,please try again later');
                }
              }}
              className="text-jen-content-primary-contrast bg-jen-interactive-primary mt-jen-spacing-32 h-[40px]"
              text="Delete Folder"
            />
            <Button
              onClick={onClose}
              className=" bg-jen-interactive-accent text-jen-interactive-primary mt-jen-spacing-8 h-[40px]"
              text="Cancel"
            />
          </div>
        );
      },
    });
  };
  const onDownloadFolder = async (name: string, id: number) => {
    Dialog.info({
      closeClassName: 'hidden',
      overlayClassName: 'bg-jen-background-primary',
      canNotCloseOnMask: true,
      dialogContentClassName: 'w-[340px]  outline-none rounded-xl bg-jen-background-primary',
      contentJSX: ({ onClose }) => {
        return (
          <div className="p-[16px] flex flex-col h-full gap-4">
            <div className=" leading-6 text-jen-content-primary text-[24px] tracking-[-0.48px]">
              Your folder tracks are being prepared for downloading.
            </div>
            <div className=" leading-5 text-jen-content-secondary text-jen-size-heading-04">
              Your folder tracks will download shortly, there will be a slight delay while the initial download is getting ready.
            </div>
            <div
              className="mt-4 h-12 rounded-lg text-jen-content-primary-contrast text-[16px] bg-jen-orange flex justify-center items-center cursor-pointer hover:opacity-90"
              onClick={() => onClose()}
            >
              OK
            </div>
          </div>
        );
      },
    });
    const { data } = await DownloadFolder({ folderId: id });
    await downloadAndZipFiles(data, `folder_${name}`);
    toast.success('Download success');
  };
  const onRenameFolder = async (name: string, id: number) => {
    try {
      await RenameFolder({ name, folderId: id });
      refreshFolders();
    } catch (error) {
      toast.error('rename error,please try again later');
    }
  };

  const onAddAudioToFolder = async (audio: AudioDto, dropId: number) => {
    try {
      await MoveAudioToFolder({ audioId: audio.id, folderId: dropId });
      refreshFolders();
      refreshAudioInList({ id: audio.id });
      refreshSongDetail();
    } catch (error) {
      toast.error('moving error,please try again later');
    }
  };
  const onMoveFolder = async (folderId: number, parentFolderId?: number, nextFolderId?: number) => {
    try {
      await SetFolderSort({ folderId, parentFolderId, nextFolderId });
      refreshFolders();
    } catch (error) {
      toast.error('moving error,please try again later');
    }
  };

  const { isShow: ifShowAddInput, setIsShow: setIfShowAddInput } = useAddNewFolderShow();

  // const [ifShowAddInput, setIfShowAddInput] = useState<boolean>(false);
  const onClickAdd = () => {
    setIfShowAddInput(true);
  };

  return (
    <div className="flex flex-col h-full pb-2">
      <div className="mt-jen-spacing-32 mb-jen-spacing-8 flex justify-between items-center pl-jen-spacing-8 pr-jen-spacing-8">
        <div className="text-jen-size-heading-07 text-jen-content-secondary">MY FOLDERS</div>
        <div>
          <a
            className="underline cursor-pointer"
            onClick={() => {
              onClickAdd();
            }}
          >
            <PlusFilledIcon fillColor="currentColor" className="text-jen-mid-grey hover:bg-jen-light-grey hover:text-black rounded-full " />
          </a>
        </div>
      </div>
      {ifShowAddInput && (
        <FolderInput
          onClose={() => {
            setIfShowAddInput(false);
          }}
          onEnter={(val) => {
            onCreateFolder(val);
            setIfShowAddInput(false);
          }}
        />
      )}

      <ScrollArea>
        {folderState?.length
          ? folderState.map((folder) => (
              <Folder
                onDelete={onDeleteFolder}
                onRename={onRenameFolder}
                onAddAudio={onAddAudioToFolder}
                onCreateFolder={onCreateFolder}
                key={folder.id}
                folder={folder}
                moveFolder={onMoveFolder}
                onDownloadFolder={onDownloadFolder}
              />
            ))
          : ''}
      </ScrollArea>
    </div>
  );
};

export default Folders;
